/**
 * One day of your calendars, for the Today screen's day grid.
 *
 * Same connections and the same Worker endpoint as the Time card
 * (calendarImport.ts), asked a different question: an explicit window around
 * one local day rather than "the last three days", all-day events kept, and
 * no minimum length — a five-minute check-in is noise on a list of things to
 * log but still a block on your day.
 */

import { useIsFetching, useQuery, useQueryClient, type Query } from "@tanstack/react-query";

import { appMode, pb } from "./pocketbase";
import { activeConnections, useCalendarSettings, type CalendarConnection } from "./calendarImport";
import { toIsoLocal } from "./timeRollup";
import { CALENDAR_PALETTE } from "./types";

/** A calendar's colour: the one picked in Settings, else by its position. */
export function calendarColor(c: Pick<CalendarConnection, "color"> | undefined, index: number): string {
  return c?.color || CALENDAR_PALETTE[index % CALENDAR_PALETTE.length];
}

export interface DayEvent {
  id: string;
  summary: string;
  /** Timed events only: instants. */
  start: Date;
  end: Date;
  allDay: boolean;
  calendarLabel: string;
  color: string;
  /** Other calendars carrying the same event — an invite on Work and Personal. */
  alsoOn?: string[];
  description?: string;
  location?: string;
  url?: string;
  /** The video-call link, when there is one. */
  joinUrl?: string;
}

export interface DayCalendar {
  connected: boolean;
  calendars: { label: string; color: string }[];
  allDay: DayEvent[];
  timed: DayEvent[];
  errors: { label: string; message: string }[];
  loading: boolean;
  failure: string | null;
}

interface Dto {
  uid: string;
  summary: string;
  start: string;
  end: string;
  calendarLabel: string;
  allDay?: boolean;
  description?: string;
  location?: string;
  url?: string;
  conference?: string;
}

// How fresh the grid is: refetched once it's this old, while the page is open
// (and on returning to the window). The Worker caches each feed for the same
// five minutes where its cache is available, so sooner would only re-read it.
const STALE_MS = 5 * 60 * 1000;
// Fetched a fortnight at a time, from the Monday of the week shown. Paging a
// day forward is then a filter on what's already here, not another round trip
// that downloads and parses every feed again.
const BLOCK_DAYS = 14;

function blockStartOf(day: Date): Date {
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

export function useDayCalendar(day: Date): DayCalendar {
  const { settings } = useCalendarSettings();
  const active = activeConnections(settings);
  const demo = appMode === "demo";
  // Colours are keyed by position in the full list, so turning one calendar
  // off doesn't repaint the others.
  const calendars = demo
    ? [{ label: "Work", color: calendarColor(undefined, 0) }, { label: "Personal", color: calendarColor(undefined, 1) }]
    : active.map((c) => ({ label: c.label, color: calendarColor(c, settings.connections.indexOf(c)) }));
  const colorOf = (label: string) => calendars.find((c) => c.label === label)?.color ?? calendarColor(undefined, 0);
  const key = toIsoLocal(day);
  const block = blockStartOf(day);

  const query = useQuery({
    queryKey: ["calendar-day", toIsoLocal(block), active.map((c) => [c.label, c.icalUrl.trim()])],
    enabled: demo || active.length > 0,
    staleTime: STALE_MS,
    refetchInterval: STALE_MS,
    refetchOnWindowFocus: true,
    retry: 1,
    queryFn: async (): Promise<{ events: Dto[]; errors: DayCalendar["errors"] }> => {
      if (demo) {
        return { events: Array.from({ length: BLOCK_DAYS }, (_, i) =>
          demoEvents(new Date(block.getFullYear(), block.getMonth(), block.getDate() + i))).flat(), errors: [] };
      }
      const token = pb?.authStore?.token;
      if (!token) throw new Error("Sign in first.");
      const from = block;
      const to = new Date(block.getFullYear(), block.getMonth(), block.getDate() + BLOCK_DAYS);
      // One request per calendar: each gets the Worker's whole time budget, and
      // a calendar that fails takes only itself down, named in the error.
      const selfEmail = (pb.authStore.model as { email?: string } | null)?.email;
      const results = await Promise.all(active.map(async (c) => {
        try {
          const res = await fetch("/api/ical/events", {
            method: "POST",
            headers: { "content-type": "application/json", Authorization: token },
            body: JSON.stringify({
              calendars: [{ label: c.label, icalUrl: c.icalUrl.trim() }],
              from: from.toISOString(),
              to: to.toISOString(),
              includeAllDay: true,
              selfEmail,
            }),
          });
          const body = await res.json().catch(() => ({})) as { events?: Dto[]; errors?: DayCalendar["errors"]; error?: string };
          if (!res.ok) {
            // A Worker that ran out of time answers with Cloudflare's own page,
            // not our JSON — say so, rather than a bare number.
            const why = body.error
              ?? (res.status === 503 || res.status === 500 ? "ran out of time reading this calendar — try again in a moment" : "");
            return { events: [], errors: [{ label: c.label, message: `sync failed (${res.status})${why ? ` — ${why}` : ""}` }] };
          }
          return { events: body.events ?? [], errors: body.errors ?? [] };
        } catch (err) {
          return { events: [], errors: [{ label: c.label, message: err instanceof Error ? err.message : "couldn't reach the calendar service" }] };
        }
      }));
      const errors = results.flatMap((r) => r.errors);
      // Nothing came back at all: a failure of the whole fetch, so the query
      // retries it and the page says so.
      if (errors.length === results.length && results.every((r) => r.events.length === 0)) {
        throw new Error(errors.map((e) => `${e.label}: ${e.message}`).join("; "));
      }
      return { events: results.flatMap((r) => r.events), errors };
    },
  });

  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
  const allDay: DayEvent[] = [];
  const timed: DayEvent[] = [];
  // One event per id. The same meeting can arrive twice — an invite that's on
  // two of your calendars, or a feed that lists an event twice — with the same
  // UID and start, so the same id. Shown once, in the first calendar's colour.
  // Two blocks with one key also confuse React's list diffing: one is left
  // behind on the grid as you page to other days.
  const seen = new Map<string, DayEvent>();
  for (const dto of query.data?.events ?? []) {
    const twin = seen.get(dto.uid);
    if (twin) {
      if (dto.calendarLabel !== twin.calendarLabel && !twin.alsoOn?.includes(dto.calendarLabel)) {
        twin.alsoOn = [...(twin.alsoOn ?? []), dto.calendarLabel];
      }
      continue;
    }
    const details = {
      calendarLabel: dto.calendarLabel,
      color: colorOf(dto.calendarLabel),
      description: dto.description ? plainText(dto.description) : undefined,
      location: dto.location,
      url: dto.url,
      joinUrl: joinLink(dto),
    };
    if (dto.allDay) {
      // Dates, end exclusive. A one-day event with no DTEND can come back with
      // end == start; it still covers its start day.
      const endKey = dto.end > dto.start ? dto.end : dto.start + "~";
      if (dto.start <= key && key < endKey) {
        const ev: DayEvent = { id: dto.uid, summary: dto.summary, start: dayStart, end: dayEnd, allDay: true, ...details };
        seen.set(dto.uid, ev);
        allDay.push(ev);
      }
      continue;
    }
    const start = new Date(dto.start);
    const end = new Date(dto.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) continue;
    if (start >= dayEnd || end <= dayStart) continue;
    const ev: DayEvent = { id: dto.uid, summary: dto.summary, start, end, allDay: false, ...details };
    seen.set(dto.uid, ev);
    timed.push(ev);
  }
  timed.sort((a, b) => a.start.getTime() - b.start.getTime() || b.end.getTime() - a.end.getTime());

  return {
    connected: demo || active.length > 0,
    calendars,
    allDay,
    timed,
    errors: query.data?.errors ?? [],
    loading: query.isLoading,
    failure: query.error instanceof Error ? query.error.message : null,
  };
}

// ---- Sync now ---------------------------------------------------------------

/** The Today grid's queries and the Time card's — everything read from your calendars. */
const isCalendarQuery = (q: Query) => q.queryKey[0] === "calendar-day" || q.queryKey[0] === "calendar-events";

/**
 * Calendar sync state for the account menu, shaped like the Todoist one: when
 * the calendars were last read, whether that's happening now, the last
 * failure, and a way to read them again. "Sync now" refetches what's on
 * screen and marks the rest stale. Where the Worker's feed cache is available
 * a feed can still be up to five minutes old, and Google itself is often
 * slower than that to update a calendar's iCal address.
 */
export function useCalendarSync(): {
  connected: boolean;
  syncing: boolean;
  lastSyncedAt: string | null;
  error: string | null;
  syncNow: () => Promise<void>;
} {
  const qc = useQueryClient();
  const { settings } = useCalendarSettings();
  // Re-renders as fetches start and finish, which is when the state below changes.
  const syncing = useIsFetching({ predicate: isCalendarQuery }) > 0;
  const queries = qc.getQueryCache().findAll({ predicate: isCalendarQuery });
  const latest = queries.reduce<Query | null>((a, q) => (!a || q.state.dataUpdatedAt > a.state.dataUpdatedAt ? q : a), null);
  const failedQuery = queries.find((q) => q.state.status === "error");
  const partial = (latest?.state.data as { errors?: { label: string; message: string }[] } | undefined)?.errors?.[0];
  return {
    connected: appMode === "demo" || activeConnections(settings).length > 0,
    syncing,
    lastSyncedAt: latest?.state.dataUpdatedAt ? new Date(latest.state.dataUpdatedAt).toISOString() : null,
    error: failedQuery?.state.error instanceof Error
      ? failedQuery.state.error.message
      : partial ? `${partial.label}: ${partial.message}` : null,
    syncNow: () => qc.invalidateQueries({ predicate: isCalendarQuery }),
  };
}

// ---- Event details ----------------------------------------------------------

/** Hosts whose links are a meeting to join, not a page about one. */
const JOIN = /https:\/\/(?:[\w-]+\.)*(?:zoom\.us\/(?:j|my|w|s)\/|meet\.google\.com\/|teams\.microsoft\.com\/(?:l\/meetup-join|meet)\/|teams\.live\.com\/meet\/|[\w-]+\.webex\.com\/|whereby\.com\/|meet\.goto\.com\/|global\.gotomeeting\.com\/join\/|app\.chime\.aws\/)[^\s<>"')\]]*/i;

/** The call link: the feed's own field first, then the location, the URL, the notes. */
function joinLink(dto: Dto): string | undefined {
  if (dto.conference) return dto.conference;
  for (const text of [dto.location, dto.url, dto.description]) {
    const m = text ? JOIN.exec(text) : null;
    if (m) return m[0].replace(/[.,;]+$/, "");
  }
  return undefined;
}

/**
 * Notes as plain text. Google sends HTML in DESCRIPTION (links as <a>, lines
 * as <br>); Outlook sends text. Links are kept as their URLs so the card can
 * linkify them; everything else becomes text.
 */
function plainText(s: string): string {
  if (!/<[a-z][\s\S]*>/i.test(s)) return s.trim();
  const doc = new DOMParser().parseFromString(
    s.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li)>/gi, "\n"), "text/html");
  doc.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href") || "";
    const text = (a.textContent || "").trim();
    // A link whose text is itself an address ("meet.google.com/abc") is just
    // the address; one with words keeps them, followed by the link.
    const textIsAddress = !text || !/\s/.test(text) && /\.[a-z]{2,}/i.test(text);
    a.replaceWith(/^https?:/i.test(href) ? (textIsAddress ? href : `${text} ${href}`) : text);
  });
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

// ---- Demo -------------------------------------------------------------------

/**
 * A plausible weekday, so the demo shows the screen rather than an empty grid.
 *
 * Invented throughout, and tied to the demo's own sample projects. The demo is
 * open to anyone from the sign-in page, so nothing here may come from a real
 * calendar — no real meeting titles, no real people's names.
 */
function demoEvents(day: Date): Dto[] {
  const dow = day.getDay();
  if (dow === 0 || dow === 6) {
    return [at(day, 9, 0, 10, 30, "Long run", "Personal")];
  }
  const out: Dto[] = [
    at(day, 8, 0, 8, 30, "Team stand-up", "Work"),
    at(day, 8, 30, 9, 0, "Inbox and planning", "Work"),
    { ...at(day, 9, 0, 10, 0, "Riverside Health — steering committee", "Work"), location: "Conference room B",
      description: "Monthly check-in with the steering group.\n\nAgenda: https://docs.example.com/riverside-agenda" },
    at(day, 10, 0, 10, 30, "Call with Sam Rivera", "Work"),
    { ...at(day, 10, 30, 11, 30, "Strategic Plan Refresh workshop", "Work"),
      description: "Join with Google Meet: <a href=\"https://meet.google.com/abc-defg-hij\">meet.google.com/abc-defg-hij</a><br>Or dial: +1 555-0100 PIN: 123456#<br><br>Draft priorities for year two and who owns each one." },
    at(day, 12, 0, 12, 30, "Lunch", "Personal"),
    at(day, 12, 30, 13, 30, "Gym", "Personal"),
    at(day, 14, 0, 14, 30, "Operations Assessment check-in", "Work"),
    { ...at(day, 14, 30, 15, 30, "Brand Positioning Sprint review", "Work"), location: "https://us02web.zoom.us/j/81234567890?pwd=demo" },
    at(day, 15, 0, 15, 30, "Dentist", "Personal"),
    at(day, 15, 30, 16, 0, "School pickup", "Personal"),
  ];
  if (dow === 5) {
    // On both calendars, as an invite often is: shows once, "also on Personal".
    out.push(at(day, 17, 0, 18, 0, "Team happy hour", "Work"), at(day, 17, 0, 18, 0, "Team happy hour", "Personal"));
  }
  if (dow === 2) {
    const k = toIsoLocal(day);
    const next = new Date(day); next.setDate(next.getDate() + 1);
    out.unshift({ uid: `demo-ooo-${k}`, summary: "Alex out of office", start: k, end: toIsoLocal(next), calendarLabel: "Work", allDay: true });
  }
  return out;
}

function at(day: Date, h1: number, m1: number, h2: number, m2: number, summary: string, calendarLabel: string): Dto {
  const s = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h1, m1);
  const e = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h2, m2);
  return { uid: `demo-${s.getTime()}-${summary}`, summary, start: s.toISOString(), end: e.toISOString(), calendarLabel };
}
